"use client"
import { 
    CheckCircle2, AlertTriangle, Pill, Heart, FlaskConical, 
    Calendar, BookOpen, ShieldAlert 
} from "lucide-react";
import { getPriorityColor } from "./utils";

export default function ManagementPlan({ managementPlan }) {
    if (!managementPlan) return null;

    const pharmacologicalGroups = Array.isArray(managementPlan.pharmacological)
        ? managementPlan.pharmacological.map((entry) => {
            const medsFromTiers = [
                ...(Array.isArray(entry?.first_line) ? entry.first_line : []),
                ...(Array.isArray(entry?.second_line) ? entry.second_line : []),
                ...(Array.isArray(entry?.third_line) ? entry.third_line : [])
            ];

            const hasTierData = medsFromTiers.length > 0;
            const fallbackFlatMeds = entry?.drug ? [entry] : [];

            return {
                ...entry,
                first_line: Array.isArray(entry?.first_line) ? entry.first_line : [],
                second_line: Array.isArray(entry?.second_line) ? entry.second_line : [],
                third_line: Array.isArray(entry?.third_line) ? entry.third_line : [],
                escalation_criteria: Array.isArray(entry?.escalation_criteria) ? entry.escalation_criteria : [],
                de_escalation_criteria: Array.isArray(entry?.de_escalation_criteria) ? entry.de_escalation_criteria : [],
                all_meds: hasTierData ? medsFromTiers : fallbackFlatMeds
            };
        })
        : [];

    const renderMedications = (medications = [], sectionTitle) => {
        if (!Array.isArray(medications) || medications.length === 0) return null;

        return (
            <div className="mt-2">
                <p className="text-xs font-semibold text-blue-900 mb-2">{sectionTitle}</p>
                <div className="space-y-2">
                    {medications.map((med, index) => (
                        <div key={index} className="p-2 sm:p-3 bg-white rounded-md border border-blue-100">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                <h5 className="text-xs sm:text-sm font-bold text-gray-900 break-words">{med?.drug || "Unnamed treatment"}</h5>
                                {med?.dose && (
                                    <span className="text-xs font-medium px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full whitespace-nowrap">
                                        {med.dose}
                                    </span>
                                )}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600">
                                {med?.route && (
                                    <div><span className="font-medium text-gray-700">Route:</span> {med.route}</div>
                                )}
                                {med?.frequency && (
                                    <div><span className="font-medium text-gray-700">Frequency:</span> {med.frequency}</div>
                                )}
                                {med?.duration && (
                                    <div><span className="font-medium text-gray-700">Duration:</span> {med.duration}</div>
                                )}
                                {med?.indication && (
                                    <div><span className="font-medium text-gray-700">Indication:</span> {med.indication}</div>
                                )}
                            </div>
                            {med?.monitoring && (
                                <p className="text-xs text-amber-700 mt-2 p-2 bg-amber-50 rounded border border-amber-100">
                                    <span className="font-medium">Monitoring:</span> {med.monitoring}
                                </p>
                            )}
                            {med?.target_response && (
                                <p className="text-xs text-emerald-700 mt-2 p-2 bg-emerald-50 rounded border border-emerald-100">
                                    <span className="font-medium">Target Response:</span> {med.target_response}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="pt-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#0B7BC1] flex-shrink-0" />
                Management Plan
            </h3>

            {/* Immediate Actions */}
            {managementPlan.immediate_actions && managementPlan.immediate_actions.length > 0 && (
                <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-orange-900 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Immediate Actions
                    </h4>
                    <div className="space-y-2">
                        {managementPlan.immediate_actions.map((item, index) => (
                            <div key={index} className="flex items-start gap-3 p-2 bg-white rounded-md border border-orange-100">
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{item.action}</p>
                                    {item.rationale && (
                                        <p className="text-xs text-gray-600 mt-1">{item.rationale}</p>
                                    )}
                                </div>
                                {item.priority && (
                                    <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${getPriorityColor(item.priority)}`}>
                                        {item.priority}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Pharmacological */}
            {pharmacologicalGroups.length > 0 && (
                <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-xs sm:text-sm font-semibold text-blue-900 mb-2 sm:mb-3 flex items-center gap-2">
                        <Pill className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                        Pharmacological Management
                    </h4>
                    <div className="space-y-2 sm:space-y-3">
                        {pharmacologicalGroups.map((group, index) => (
                            <div key={index} className="p-2 sm:p-3 bg-blue-100/40 rounded-md border border-blue-200">
                                {group?.condition && (
                                    <div className="mb-2">
                                        <p className="text-sm font-semibold text-gray-900 break-words">
                                            {group.condition}
                                        </p>
                                    </div>
                                )}

                                {group?.treatment_approach && (
                                    <p className="text-xs text-blue-900 bg-white p-2 rounded border border-blue-100 mb-2">
                                        <span className="font-semibold">Treatment Approach:</span> {group.treatment_approach}
                                    </p>
                                )}

                                {renderMedications(group.first_line, "First-line")}
                                {renderMedications(group.second_line, "Second-line")}
                                {renderMedications(group.third_line, "Third-line")}

                                {(!group.first_line?.length && !group.second_line?.length && !group.third_line?.length) &&
                                    renderMedications(group.all_meds, "Medications")
                                }

                                {group.escalation_criteria.length > 0 && (
                                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                                        <p className="text-xs font-semibold text-amber-900 mb-1">Escalation Criteria</p>
                                        <ul className="space-y-1">
                                            {group.escalation_criteria.map((item, escIdx) => (
                                                <li key={escIdx} className="text-xs text-amber-800 flex gap-2">
                                                    <span className="text-amber-500 flex-shrink-0">•</span>
                                                    <span className="break-words">{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {group.de_escalation_criteria.length > 0 && (
                                    <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded">
                                        <p className="text-xs font-semibold text-emerald-900 mb-1">De-escalation Criteria</p>
                                        <ul className="space-y-1">
                                            {group.de_escalation_criteria.map((item, deEscIdx) => (
                                                <li key={deEscIdx} className="text-xs text-emerald-800 flex gap-2">
                                                    <span className="text-emerald-500 flex-shrink-0">•</span>
                                                    <span className="break-words">{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Non-Pharmacological */}
            {managementPlan.non_pharmacological && managementPlan.non_pharmacological.length > 0 && (
                <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="text-xs sm:text-sm font-semibold text-green-900 mb-2 flex items-center gap-2">
                        <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                        Non-Pharmacological
                    </h4>
                    <ul className="space-y-1">
                        {managementPlan.non_pharmacological.map((item, index) => (
                            <li key={index} className="text-xs sm:text-sm text-green-800 flex gap-2">
                                <span className="text-green-400 flex-shrink-0">•</span>
                                <span className="break-words">{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Investigations Ordered */}
            {managementPlan.investigations_ordered && managementPlan.investigations_ordered.length > 0 && (
                <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h4 className="text-xs sm:text-sm font-semibold text-purple-900 mb-2 sm:mb-3 flex items-center gap-2">
                        <FlaskConical className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                        Investigations Ordered
                    </h4>
                    <div className="space-y-2">
                        {managementPlan.investigations_ordered.map((inv, index) => (
                            <div key={index} className="flex flex-col sm:flex-row items-start gap-2 sm:gap-3 p-2 bg-white rounded-md border border-purple-100">
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs sm:text-sm font-medium text-gray-900 break-words">{inv.test}</p>
                                    {inv.rationale && (
                                        <p className="text-xs text-gray-600 mt-1 break-words">{inv.rationale}</p>
                                    )}
                                </div>
                                {inv.urgency && (
                                    <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${getPriorityColor(inv.urgency)}`}>
                                        {inv.urgency}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Follow-Up */}
            {managementPlan.follow_up && (
                <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <h4 className="text-xs sm:text-sm font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                        Follow-Up
                    </h4>
                    <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4 mb-2">
                        {managementPlan.follow_up.timeframe && (
                            <div className="text-xs sm:text-sm text-indigo-800">
                                <span className="font-medium">Timeframe:</span> {managementPlan.follow_up.timeframe}
                            </div>
                        )}
                        {managementPlan.follow_up.with && (
                            <div className="text-xs sm:text-sm text-indigo-800">
                                <span className="font-medium">With:</span> {managementPlan.follow_up.with}
                            </div>
                        )}
                    </div>
                    {managementPlan.follow_up.goals && managementPlan.follow_up.goals.length > 0 && (
                        <div className="mt-2">
                            <p className="text-xs font-medium text-indigo-900 mb-1">Goals:</p>
                            <ul className="space-y-1">
                                {managementPlan.follow_up.goals.map((goal, index) => (
                                    <li key={index} className="text-xs text-indigo-800 flex gap-1.5">
                                        <span className="text-indigo-400 flex-shrink-0">•</span>
                                        <span className="break-words">{goal}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* Patient Education */}
            {managementPlan.patient_education && managementPlan.patient_education.length > 0 && (
                <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-teal-50 border border-teal-200 rounded-lg">
                    <h4 className="text-xs sm:text-sm font-semibold text-teal-900 mb-2 flex items-center gap-2">
                        <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                        Patient Education
                    </h4>
                    <ul className="space-y-1">
                        {managementPlan.patient_education.map((item, index) => (
                            <li key={index} className="text-xs sm:text-sm text-teal-800 flex gap-2">
                                <span className="text-teal-400 flex-shrink-0">•</span>
                                <span className="break-words">{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Red Flags to Watch */}
            {managementPlan.red_flags_to_watch && managementPlan.red_flags_to_watch.length > 0 && (
                <div className="p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="text-xs sm:text-sm font-semibold text-red-900 mb-2 flex items-center gap-2">
                        <ShieldAlert className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                        Red Flags to Watch
                    </h4>
                    <ul className="space-y-1">
                        {managementPlan.red_flags_to_watch.map((flag, index) => (
                            <li key={index} className="text-xs sm:text-sm text-red-800 flex gap-2">
                                <span className="text-red-400 flex-shrink-0">•</span>
                                <span className="break-words">{flag}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
